import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { DoubleDoorType, WallType } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from "./SelectableSymbol";
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import AttachableSymbol from "./AttachableSymbol";
import { Line } from "@react-three/drei";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { set, transaction } from "mobx";
import { is } from '@react-three/fiber/dist/declarations/src/core/utils';
import { AttachmentMeasurementLines } from "./AttachmentMeasurementLines";
import { getSymbolRenderTotalWidth } from "../../utils/symbolAttachmentGeometry";
import {
  getWallAttachmentRenderOrder,
  getWallAttachmentZOffset,
  usePlanarWallAttachmentLayer,
} from "./wallAttachmentRenderLayer";
import { CANVAS_ACCENT_COLOR, canvasAccentColor, canvasHandleFillColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import ObjectOpacityGroup from "./ObjectOpacityGroup";

export const defaultOpenAngle = Math.PI / 2;
export const defaultDoorWidth = 1;

interface DoubleDoorProps {
  doubleDoor: DoubleDoorType;
  attachedToWall?: WallType;
  onDragStart: (doubleDoor: DoubleDoorType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const DoubleDoor: React.FC<DoubleDoorProps> = observer(({
  doubleDoor,
  attachedToWall,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const [currentOpenAngle, setCurrentOpenAngle] = useState(doubleDoor.openAngle);
  const currentOpenAngleRef = useRef(currentOpenAngle); // Ref for the current open angle in event listeners
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const wallAttachmentLayerRef = useRef<THREE.Group>(null);
  const attachableRef = useRef<{ checkForAttachment: () => void }>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const isAttachedToWall = Boolean(attachedToWall);
  const wallAttachmentRenderOrder = getWallAttachmentRenderOrder(doubleDoor.zIndex);

  usePlanarWallAttachmentLayer(
    wallAttachmentLayerRef,
    isAttachedToWall,
    wallAttachmentRenderOrder,
  );

  const setWallAttachmentLayerRef = useCallback((node: THREE.Group | null) => {
    groupRef.current = node;
    wallAttachmentLayerRef.current = node;
  }, []);

  const lineWeight = doubleDoor.lineWeight || floorplannerStore.doorLineWeight;
  const frameThickness = doubleDoor.doorFrameWidth || floorplannerStore.doorFrameWidth;
  const bladeThickness = doubleDoor.doorBladeThickness || floorplannerStore.doorBladeThickness;
  const [doubleDoorWidth, setDoubleDoorWidth] = useState(doubleDoor.doubleDoorWidth || floorplannerStore.doorWidth);
  const grabHandleSize = 0.12;
  const handleRadius = useMemo(() => grabHandleSize / editorStore.zoomLevelDivisor(), [editorStore.zoomLevel]);
  const handleDepth = 0.02;
  const flipX = 1;
  const flipY = doubleDoor.flipVertical ? -1 : 1;
  const [centerPosition, setCenterPosition] = useState<[number, number]>([
    doubleDoorWidth / 2,
    doubleDoorWidth / 2,
  ]);
  const lineColor = doubleDoor.lineColor || floorplannerStore.lineColor;
  const wallWidth = attachedToWall?.wallWidth || floorplannerStore.wallWidth;
  const wallLineWeight = floorplannerStore.convertLineWeightToWorld(attachedToWall?.lineWeight || floorplannerStore.wallLineWeight)
  const wallLineColor = attachedToWall?.lineColor || floorplannerStore.lineColor;
  const doorFrameWidth = doubleDoor.doorFrameWidth || floorplannerStore.doorFrameWidth;
  const attachment = attachedToWall?.symbolAttachments?.find((item) => item.symbolId === doubleDoor.id);
  const renderedLeafWidth = useMemo(() => {
    if (!attachedToWall || !attachment) {
      return doubleDoorWidth;
    }
    const openingWidth = getSymbolRenderTotalWidth(
      attachedToWall,
      doubleDoor,
      attachment.positionFromStart,
      floorplannerStore.doorFrameWidth,
    );
    return Math.max((openingWidth - frameThickness * 2) / 2, 0.01);
  }, [attachedToWall, attachment?.positionFromStart, doubleDoor, doubleDoorWidth, floorplannerStore.doorFrameWidth, frameThickness]);

  const hingePosition1 = useMemo(() => {
    return doubleDoor.flipHorizontal
      ? attachedToWall ? [renderedLeafWidth, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [renderedLeafWidth, 0, 0]
      : attachedToWall ? [0, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [0, 0, 0];
  }, [attachedToWall, bladeThickness, doubleDoor.flipHorizontal, flipY, renderedLeafWidth, wallLineWeight]);

  const hingePosition2 = useMemo(() => {
    return doubleDoor.flipHorizontal
      ? attachedToWall ? [renderedLeafWidth, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [renderedLeafWidth, 0, 0]
      : attachedToWall ? [renderedLeafWidth * 2, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [renderedLeafWidth * 2, 0, 0];
  }, [attachedToWall, bladeThickness, doubleDoor.flipHorizontal, flipY, renderedLeafWidth, wallLineWeight]);

  const openAngleGrabHandleX = useMemo(() => {
    const hingeOffsetX = 0;
    return hingeOffsetX + renderedLeafWidth * 0.6 * Math.cos(currentOpenAngle) * flipX;
  }, [currentOpenAngle, flipX, renderedLeafWidth]);

  const openAngleGrabHandleY = useMemo(() => {
    return renderedLeafWidth * 0.6 * Math.sin(currentOpenAngle) * flipY;
  }, [currentOpenAngle, flipY, renderedLeafWidth]);

  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingAngle, setIsDraggingAngle] = useState(false);
  const [hoveringHandle, setHoveringHandle] = useState<string | null>(null);
  const noopRaycast = () => null;
  const aboutToModify = useRef(false);
  const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) return { topLeft: [0, 0], topRight: [0, 0], bottomLeft: [0, 0], bottomRight: [0, 0], width: 0, height: 0, depth: 0 };

    // Calculate the actual bounds of the double door symbol based on the open angle and flip state
    // A double door has two hinges - one at x=0 and one at x=doubleDoorWidth*2
    // The left door swings one way, the right door swings the other way (mirrored)

    // Total width is 2x the door width (two door panels)
    const totalDoorWidth = renderedLeafWidth * 2;

    // Calculate how far the arcs extend based on the current open angle
    // For double doors, both arcs extend vertically the same amount
    const arcExtentY = renderedLeafWidth * Math.sin(currentOpenAngle);

    // X bounds: from 0 to total door width (both doors open inward/outward but stay within the frame width mostly)
    let minX = 0;
    let maxX = totalDoorWidth;

    // Calculate the Y bounds based on flip state
    // The arc extends based on the current open angle, not the full door width
    let minY: number, maxY: number;
    if (doubleDoor.flipVertical) {
      // Door swings downward (negative Y)
      minY = -arcExtentY;
      maxY = bladeThickness; // Small amount for the blade thickness at hinge
    } else {
      // Door swings upward (positive Y)
      minY = -bladeThickness; // Small amount for the blade thickness
      maxY = arcExtentY;
    }

    // Adjust bounds if attached to wall (add frame thickness)
    if (attachedToWall) {
      minX -= frameThickness + wallLineWeight;
      maxX += frameThickness + wallLineWeight;
      // The wall width extends equally above and below the door line
      const halfWallWidth = wallWidth / 2 + wallLineWeight;
      if (doubleDoor.flipVertical) {
        minY = Math.min(minY, -halfWallWidth);
        maxY = Math.max(maxY, halfWallWidth);
      } else {
        minY = Math.min(minY, -halfWallWidth);
        maxY = Math.max(maxY, halfWallWidth);
      }
    }

    const width = maxX - minX;
    const height = maxY - minY;

    // Center position is the center of the bounding box
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    setCenterPosition([centerX, centerY]);

    return {
      topLeft: [minX, maxY],
      topRight: [maxX, maxY],
      bottomLeft: [minX, minY],
      bottomRight: [maxX, minY],
      width: width,
      height: height,
      depth: 0,
    };
  }, [attachedToWall, bladeThickness, currentOpenAngle, doubleDoor.flipVertical, frameThickness, renderedLeafWidth, wallLineWeight, wallWidth]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = useCallback((event: PointerEvent) => {
    if (aboutToModify.current) {
      floorplannerStore.beginUndoTransaction('updateOpenAngle');
      floorplannerStore.recordSymbolForUndo(doubleDoor.id);
      aboutToModify.current = false;
    }
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const doorRotation = doubleDoor.rotation || 0;

    const mousePosition = new THREE.Vector2(worldX, worldY);
    const doorPosition = new THREE.Vector2(doubleDoor.position.x, doubleDoor.position.y);

    // Get mouse position relative to door origin
    const offset = mousePosition.clone().sub(doorPosition);
    // Rotate to door's local coordinate system
    offset.rotateAround(new THREE.Vector2(0, 0), -doorRotation);

    // For double doors, the handle is positioned at the first hinge (left side)
    // Calculate the hinge position in local coordinates
    const hingeX = doubleDoor.flipHorizontal ? renderedLeafWidth : 0;
    const hingeY = 0;

    // Calculate the vector from hinge to mouse in local space
    const dx = (offset.x - hingeX) * flipX;
    const dy = (offset.y - hingeY) * flipY;

    // Calculate angle from hinge to mouse position
    let angle = Math.atan2(dy, dx);

    // Normalize angle to 0 to 2*PI range
    if (angle < 0) angle += 2 * Math.PI;

    // Clamp angle to valid door opening range (0 to 90 degrees)
    if (angle > Math.PI) {
      angle = 0;
    } else if (angle > Math.PI / 2) {
      angle = Math.PI / 2;
    }

    setCurrentOpenAngle(angle);
  }, [doubleDoor.position.x, doubleDoor.position.y, doubleDoor.rotation, doubleDoor.flipHorizontal, flipX, flipY, gl, camera, renderedLeafWidth]);

  const onPointerDownHandle = useCallback((event: React.PointerEvent) => {
    event.stopPropagation();
    aboutToModify.current = true;
    setIsDraggingAngle(true);
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    dragOffset.current = {
      x: worldX - (renderedLeafWidth * 0.75 * Math.cos(currentOpenAngle) * flipX + doubleDoor.position.x),
      y: worldY - (renderedLeafWidth * 0.75 * Math.sin(currentOpenAngle) * flipY + doubleDoor.position.y),
    };
    gl.domElement.addEventListener("pointermove", onHandleDrag);
    gl.domElement.addEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(true);
  }, [onHandleDrag, currentOpenAngle, doubleDoor.position.x, doubleDoor.position.y, flipX, flipY, gl, renderedLeafWidth]);

  const onPointerUpHandle = useCallback(() => {
    aboutToModify.current = false;
    setIsDraggingAngle(false);
    if (doubleDoor.openAngle !== currentOpenAngleRef.current) {
      floorplannerStore.updateSymbolProperty(doubleDoor.id, "openAngle", currentOpenAngleRef.current);
      floorplannerStore.recordSymbolChange(doubleDoor.id, 'modify');
      floorplannerStore.pushToUndoStack();
    }
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  }, [doubleDoor.openAngle, onHandleDrag, floorplannerStore, gl]);

  const handleDrag = useCallback((newPosition: [number, number]) => {
    onDrag(newPosition);
  }, [onDrag]);

  const generateArcPoints = useMemo(() => {
    const arcPoints = [];
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * currentOpenAngle;
      arcPoints.push(new THREE.Vector3(
        renderedLeafWidth * Math.cos(angle) * flipX,
        renderedLeafWidth * Math.sin(angle) * flipY,
        0
      ));
    }
    return arcPoints;
  }, [currentOpenAngle, flipX, flipY, renderedLeafWidth]);

  const generateMirrorArcPoints = useMemo(() => {
    const arcPoints = [];
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * currentOpenAngle;
      arcPoints.push(new THREE.Vector3(
        renderedLeafWidth * Math.cos(angle) * -flipX,
        renderedLeafWidth * Math.sin(angle) * flipY,
        0
      ));
    }
    return arcPoints;
  }, [currentOpenAngle, flipX, flipY, renderedLeafWidth]);

  const doorBladePoints = useMemo(() => [
    [0, -bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, -bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeThickness * flipY - bladeThickness, 0] as [number, number, number],
    [0, bladeThickness * flipY - bladeThickness, 0] as [number, number, number],
    [0, -bladeThickness, 0] as [number, number, number],
  ], [bladeThickness, flipX, flipY, renderedLeafWidth]);

  const doorBladePointsFlipY = useMemo(() => [
    [0, bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeThickness, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeThickness * flipY + bladeThickness, 0] as [number, number, number],
    [0, bladeThickness * flipY + bladeThickness, 0] as [number, number, number],
    [0, bladeThickness, 0] as [number, number, number],
  ], [bladeThickness, flipX, flipY, renderedLeafWidth]);

  const doorBladePointsMirror = useMemo(() => [
    [0, 0, 0] as [number, number, number],
    [renderedLeafWidth * flipX, 0, 0] as [number, number, number],
    [renderedLeafWidth * flipX, bladeThickness * flipY, 0] as [number, number, number],
    [0, bladeThickness * flipY, 0] as [number, number, number],
    [0, 0, 0] as [number, number, number],
  ], [bladeThickness, flipX, flipY, renderedLeafWidth]);

  const generateFramePoints = useCallback((wallWidth: number, side: number): [number, number, number][] => {
    const offset = (side * renderedLeafWidth + renderedLeafWidth);
    return [
      [offset, -wallWidth / 2, 0],
      [offset + frameThickness * side, -wallWidth / 2, 0],
      [offset + frameThickness * side, wallWidth / 2, 0],
      [offset, wallWidth / 2, 0],
      [offset, -wallWidth / 2, 0], // Closing the frame
    ];
  }, [frameThickness, renderedLeafWidth]);

  useEffect(() => {
    setCurrentOpenAngle(doubleDoor.openAngle);
  }, [doubleDoor.openAngle]);

  useEffect(() => {
    setDoubleDoorWidth(doubleDoor.doubleDoorWidth);
  }, [doubleDoor.doubleDoorWidth]);

  // Update the ref whenever currentOpenAngle changes
  useEffect(() => {
    currentOpenAngleRef.current = currentOpenAngle;
  }, [currentOpenAngle]);

  useEffect(() => {
    editorStore.updateGroupRef(doubleDoor.id, groupRef.current);
  }, [doubleDoor.id, floorplannerStore]);

  return (
    <group
      position={[0, 0, getWallAttachmentZOffset(isAttachedToWall, doubleDoor.zIndex)]}
    >
      {/* Attachment measurement lines - show distance from wall ends while dragging */}
      {attachedToWall && (
        <AttachmentMeasurementLines
          symbol={doubleDoor}
          attachedWall={attachedToWall}
          isDragging={isDragging}
        />
      )}
      <DraggableObject
        position={[doubleDoor.position.x, doubleDoor.position.y]}
        onDragStart={(offset) => {
          onDragStart(doubleDoor, offset);
          setIsDragging(true);
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={doubleDoor.id}
        attachmentType="doorAttachments"
        symbol={doubleDoor}
      >
        <AttachableSymbol
          attachmentId={doubleDoor.id}
          attachmentType="doorAttachments"
          onAttachment={(attachPosition) => {
            // Handle attachment logic
          }}
          store={floorplannerStore}
          ref={attachableRef}
        >
          <SelectableSymbol
            ref={groupRef}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResizeEnd={() => setIsResizing(false)}
            onResize={(newWidth, newHeight, boundingBox, handle) => {
              // Calculate the change in bounding box width
              const widthDelta = newWidth - boundingBox.width;

              // For double doors, each panel changes by half the total delta
              const panelWidthDelta = widthDelta / 2;

              // Apply the delta to the current door width
              const newDoorWidth = Math.max(doubleDoorWidth + panelWidthDelta, 0.1);
              const widthDiff = newDoorWidth - doubleDoorWidth;

              // Determine which side should stay anchored based on handle
              const isLeftHandle = handle === "topLeft" || handle === "bottomLeft";

              // Double doors have their origin at the left edge (x=0)
              // When dragging left handles, we need to shift position to keep the right side fixed
              let positionDeltaX = 0;
              if (isLeftHandle) {
                positionDeltaX = -widthDiff * 2; // *2 because total width change is 2x panel change
              }

              transaction(() => {
                floorplannerStore.updateSymbolProperty(doubleDoor.id, "doubleDoorWidth", newDoorWidth);

                if (positionDeltaX !== 0) {
                  const position = [
                    doubleDoor.position.x + positionDeltaX * Math.cos(doubleDoor.rotation || 0),
                    doubleDoor.position.y + positionDeltaX * Math.sin(doubleDoor.rotation || 0),
                  ];
                  if (attachedToWall) {
                    floorplannerStore.detachSymbolFromWall(doubleDoor.id);
                    floorplannerStore.updateSymbolProperty(doubleDoor.id, "position", position);
                    floorplannerStore.attachSymbolToWall(doubleDoor.id, attachedToWall.id, [position[0], position[1]]);
                  } else {
                    floorplannerStore.updateSymbolProperty(doubleDoor.id, "position", position);
                  }
                }
              });
            }}
            center={centerPosition}
            rotation={doubleDoor.rotation}
            symbol={doubleDoor}
            onPointerOver={() => setIsHovered(true)}
            onPointerOut={() => setIsHovered(false)}
            isDragging={isDragging}
          >
            <group
              ref={setWallAttachmentLayerRef}
              rotation={[0, 0, doubleDoor.rotation || 0]}
              renderOrder={isAttachedToWall ? wallAttachmentRenderOrder : undefined}
              onPointerEnter={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
              }}
              onPointerLeave={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
              }}
            >
              {/* Invisible box to catch events on any unfilled areas */}
              <mesh
                position={[
                  renderedLeafWidth,
                  (renderedLeafWidth / 2) * flipY,
                  -0.03
                ]}
                rotation={[0, 0, 0]}
                onPointerEnter={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
                }}
                onPointerLeave={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
                }}
              >
                <boxGeometry args={[renderedLeafWidth * 2, renderedLeafWidth, 0.06]} />
                <meshBasicMaterial
                  //color="white"
                  transparent
                  opacity={0.0}
                />
              </mesh>
              <ObjectOpacityGroup
                key={`double-door-opacity-${Math.round(resolveFillOpacity(doubleDoor) * 100)}-${getOpacityRemountEpoch(doubleDoor.id)}`}
                opacity={resolveFillOpacity(doubleDoor)}
              >
              {attachedToWall && (
                <>
                  {/* <mesh position={[-doorFrameWidth - wallLineWeight / 2, 0, 0]}>
                  <boxGeometry args={[wallLineWeight, wallWidth + wallLineWeight, 0.001]} />
                  <meshStandardMaterial color={wallLineColor} />
                </mesh>
                <mesh position={[doubleDoorWidth * 2 + doorFrameWidth + wallLineWeight / 2, 0, 0]}>
                  <boxGeometry args={[wallLineWeight, wallWidth + wallLineWeight, 0.001]} />
                  <meshStandardMaterial color={wallLineColor} />
                </mesh> */}
                </>
              )}

              <Line
                position={new THREE.Vector3(hingePosition1[0], hingePosition1[1], hingePosition1[2])}
                points={generateArcPoints.map((point) => [point.x, point.y, point.z])}
                color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                lineWidth={lineWeight / 2}
                {...(doubleDoor.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              <Line
                position={new THREE.Vector3(hingePosition2[0], hingePosition2[1], hingePosition2[2])}
                points={generateMirrorArcPoints.map((point) => [point.x, point.y, point.z])}
                color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                lineWidth={lineWeight / 2}
                {...(doubleDoor.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              <Line
                points={flipY === 1 ? doorBladePoints : doorBladePointsFlipY}
                color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                lineWidth={lineWeight}
                position={new THREE.Vector3(hingePosition1[0], hingePosition1[1], hingePosition1[2])}
                rotation={[0, 0, currentOpenAngle * flipX * flipY]}
                {...(doubleDoor.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />
              <Line
                points={doorBladePointsMirror}
                color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                lineWidth={lineWeight}
                position={new THREE.Vector3(hingePosition2[0], hingePosition2[1], hingePosition2[2])}
                rotation={[0, 0, Math.PI - currentOpenAngle * flipX * flipY]}
                {...(doubleDoor.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              {attachedToWall && (
                <>
                  <Line
                    points={generateFramePoints(wallWidth, -1)}
                    color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                    lineWidth={1}
                    {...(doubleDoor.lineType === "dashed" && {
                      dashed: true,
                      dashSize: 0.15,
                      gapSize: 0.1,
                    })}
                    raycast={noopRaycast}
                  />
                  <Line
                    points={generateFramePoints(wallWidth, 1)}
                    color={canvasAccentColor(doubleDoor.selected, !!isHovered, lineColor)}
                    lineWidth={1}
                    {...(doubleDoor.lineType === "dashed" && {
                      dashed: true,
                      dashSize: 0.15,
                      gapSize: 0.1,
                    })}
                    raycast={noopRaycast}
                  />
                </>
              )}
              </ObjectOpacityGroup>

              {(selectionHandlesVisible && doubleDoor.selected && !isResizing && !isDragging && editorStore.selections.length === 1) && (
                <group
                  onPointerDown={onPointerDownHandle as unknown as (event: THREE.Event) => void}
                  onPointerUp={onPointerUpHandle as unknown as (event: THREE.Event) => void}
                  onPointerOver={() => setHoveringHandle("openAngle")}
                  onPointerOut={() => setHoveringHandle(null)}
                  position={[
                    openAngleGrabHandleX,
                    openAngleGrabHandleY,
                    0,
                  ]}
                >
                  <mesh>
                    <torusGeometry args={[handleRadius - 0.002, 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[handleRadius - 0.02, handleRadius - 0.006, handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(isDraggingAngle, hoveringHandle === "openAngle")} />
                  </mesh>
                </group>
              )}
            </group>
          </SelectableSymbol>
        </AttachableSymbol>
      </DraggableObject>
    </group>
  );
});

export default DoubleDoor;
